import {
  tableMemo,
  type Row,
  type RowData,
  type RowModel,
  type Table,
} from '@tanstack/react-table';

import type { TDataTableFeatures } from '../types/features.types.js';
import type { TableMemoTable } from '../types/table.types.js';

/**
 * Creates a custom pagination row model.
 *
 * @returns A function that takes a table instance and returns a memoed function to get the paginated row model.
 */
export function createPaginatedRowModel<
  // oxlint-disable-next-line typescript/no-explicit-any
  TData extends RowData = any,
>(): (
  table: Table<TDataTableFeatures, TData>,
) => () => RowModel<TDataTableFeatures, TData> {
  return (table) => {
    return tableMemo({
      feature: 'rowPaginationFeature',
      table: table as unknown as TableMemoTable,
      fnName: 'table.getPaginatedRowModel',
      memoDeps: () => [
        table.getPrePaginatedRowModel(),
        table.atoms.pagination?.get(),
        table.options.paginateExpandedRows
          ? table.atoms.expanded?.get()
          : undefined,
      ],
      fn: () => _createPaginatedRowModel(table),
    });
  };
}

/**
 * Creates a custom pagination row model.
 */
function _createPaginatedRowModel<
  // oxlint-disable-next-line typescript/no-explicit-any
  TData extends RowData = any,
>(
  table: Table<TDataTableFeatures, TData>,
): RowModel<TDataTableFeatures, TData> {
  const prePaginatedRowModel = table.getPrePaginatedRowModel();
  const pagination = table.atoms.pagination?.get();

  if (
    !prePaginatedRowModel.rows.length ||
    table.options.enablePagination !== true
  ) {
    return prePaginatedRowModel;
  }

  const { pageSize, pageIndex } = pagination;
  const { rowsById } = prePaginatedRowModel;
  const pageStart = pageSize * pageIndex;
  const pageEnd = pageStart + pageSize;

  const rows = prePaginatedRowModel.rows.slice(pageStart, pageEnd);

  const paginatedRowModel: RowModel<TDataTableFeatures, TData> = {
    rows,
    flatRows: [],
    rowsById,
  };

  // keep track of the already added flatRows, to avoid duplication of already expanded sub-rows
  const addedFlatRowsSet = new Set();

  const handleRow = (row: Row<TDataTableFeatures, TData>) => {
    if (addedFlatRowsSet.has(row.id)) {
      return;
    }

    paginatedRowModel.flatRows.push(row);
    addedFlatRowsSet.add(row.id);

    if (row.subRows.length) {
      row.subRows.forEach(handleRow);
    }
  };

  paginatedRowModel.rows.forEach(handleRow);

  return paginatedRowModel;
}
